import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { DASHBOARD_COLORS } from "../colors";
import { useDrillDownDialog } from "../DrillDownProvider";
import { DrillDownConfig, WidgetExplanationContent } from "../types";
import { WidgetExplanation } from "./WidgetExplanation";
import { whiteCardSx } from "./widgetStyles";

type ComparisonCardProps = {
    label: string;
    value: number | undefined;
    previousValue: number | undefined;
    previousLabel: string;
    explanation: WidgetExplanationContent;
    isLoading?: boolean;
    drillDown?: DrillDownConfig;
};

export const ComparisonCard = ({ label, value, previousValue, previousLabel, explanation, isLoading, drillDown }: ComparisonCardProps) => {
    const isUp = (value ?? 0) >= (previousValue ?? 0);
    const trendColor = isUp ? DASHBOARD_COLORS.done : DASHBOARD_COLORS.cancelled;
    const drillDownDialog = useDrillDownDialog();
    const isClickable = Boolean(drillDown && drillDownDialog && !isLoading);

    return (
        <Paper
            elevation={0}
            onClick={isClickable ? () => drillDownDialog!.openDrillDown(drillDown!) : undefined}
            sx={{
                ...whiteCardSx,
                p: 2,
                height: "100%",
                textAlign: "center",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                ...(isClickable ? { cursor: "pointer", "&:hover": { backgroundColor: "#f7f8fa" } } : {}),
            }}
        >
            <Box position="absolute" top={4} right={4}>
                <WidgetExplanation title={label} explanation={explanation} />
            </Box>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {label}
            </Typography>
            {isLoading ? (
                <Skeleton variant="text" width={64} sx={{ fontSize: "1.6rem", mx: "auto" }} />
            ) : (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <Typography variant="h5" fontWeight={700}>
                        {value ?? "—"}
                    </Typography>
                    {isUp ? (
                        <ArrowUpward fontSize="small" sx={{ color: trendColor }} />
                    ) : (
                        <ArrowDownward fontSize="small" sx={{ color: trendColor }} />
                    )}
                </Box>
            )}
            <Typography variant="caption" color="text.secondary">
                {previousLabel}: {isLoading ? "…" : previousValue ?? "—"}
            </Typography>
        </Paper>
    );
};
